"use client";

import { useState } from "react";
import { useSession } from "next-auth/react";
import { useCommerceSettings } from "@/contexts/CommerceSettingsContext";
import { useRouter, useParams } from "next/navigation";
import Link from "next/link";
import { useTranslations } from "next-intl";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import { toast } from "react-toastify";

export default function TeamsSignupPage() {
    const params = useParams();
    const locale = params.locale;
    const { data: session } = useSession();
    const router = useRouter();
    const t = useTranslations("teams");
    const [loading, setLoading] = useState(false);

    const [formData, setFormData] = useState({
        teamName: "",
        slug: "",
        seats: 10,
    });

    const [errors, setErrors] = useState({});
    const [serverError, setServerError] = useState(null);
    // Teams est réservé aux professionnels (CGV, article 13) : l'acheteur
    // le déclare avant de payer.
    const [professionalChecked, setProfessionalChecked] = useState(false);
    const [showProfessionalError, setShowProfessionalError] = useState(false);

    const PRICE_PER_SEAT_MONTHLY =
        parseInt(process.env.NEXT_PUBLIC_PRICE_PER_SEAT_MONTHLY_FOR_TEAMS) ||
        20;
    const PRICE_PER_SEAT_YEARLY = PRICE_PER_SEAT_MONTHLY * 12;
    // Essai Teams : interrupteur et durée pilotés depuis /admin/promotions.
    // Le serveur applique le même réglage au moment de créer l'abonnement.
    const { settings: commerceSettings } = useCommerceSettings();
    const FREE_TRIAL_DAYS = commerceSettings.trial.teams.days;
    const SHOULD_OFFER_FREE_TRIAL = commerceSettings.trial.teams.enabled;

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        if (name === "teamName") {
            const autoSlug = value
                .toLowerCase()
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "")
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/^-+|-+$/g, "");
            setFormData((prev) => ({
                ...prev,
                slug: autoSlug,
            }));
        }

        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: null }));
        }
    };

    const handleSeatsChange = (delta) => {
        setFormData((prev) => ({
            ...prev,
            seats: Math.max(1, Math.min(1000, prev.seats + delta)),
        }));
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.teamName.trim()) {
            newErrors.teamName = t("signupTeamNameRequired");
        }

        if (!formData.slug.trim()) {
            newErrors.slug = t("signupSlugRequired");
        } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
            newErrors.slug = t("signupSlugInvalid");
        }

        if (formData.seats < 1) {
            newErrors.seats = t("signupSeatsMin");
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!session?.user) {
            toast.error(t("signupLoginRequired"));
            router.push(`/${locale}/login?redirect=/teams/signup`);
            return;
        }

        if (!validateForm()) {
            return;
        }

        if (!professionalChecked) {
            setShowProfessionalError(true);
            return;
        }

        setLoading(true);
        setServerError(null);

        try {
            const response = await fetch("/api/teams/create-checkout-session", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    teamName: formData.teamName,
                    slug: formData.slug,
                    seats: formData.seats,
                    userId: session.user.userId,
                    professionalPurchase: true,
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                // Si c'est une erreur de slug, l'afficher sous le champ slug
                if (data.message?.toLowerCase().includes("slug")) {
                    setErrors((prev) => ({ ...prev, slug: data.message }));
                } else {
                    setServerError(data.message || t("signupErrorGeneric"));
                }
                setLoading(false);
                return;
            }

            window.location.href = data.checkoutUrl;
        } catch (error) {
            console.error("Error:", error);
            setServerError(t("signupErrorGeneric"));
            setLoading(false);
        }
    };

    const totalPrice = formData.seats * PRICE_PER_SEAT_YEARLY;
    const monthlyTotal = formData.seats * PRICE_PER_SEAT_MONTHLY;

    const features = [
        t("signupFeature1"),
        t("signupFeature2"),
        t("signupFeature3"),
        t("signupFeature4"),
        t("signupFeature5"),
        t("signupFeature6"),
    ];

    return (
        <div className="min-h-screen bg-white">
            <div className="max-w-5xl mx-auto px-4 py-16 md:py-24">
                {/* Header */}
                <div className="text-center mb-12">
                    <h1 className="text-3xl md:text-4xl font-semibold text-gray-900 tracking-tight">
                        {t("signupTitle")}
                    </h1>
                    {SHOULD_OFFER_FREE_TRIAL && (
                        <p className="mt-3 text-lg text-gray-500">
                            {t("signupBadge", { days: FREE_TRIAL_DAYS })}
                        </p>
                    )}
                </div>

                <div className="grid md:grid-cols-2 gap-12 md:gap-16">
                    {/* Left: Form */}
                    <div>
                        <form onSubmit={handleSubmit} className="space-y-8">
                            {/* Server Error */}
                            {serverError && (
                                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                                    <p className="text-red-600 text-sm mb-0">
                                        {serverError}
                                    </p>
                                </div>
                            )}

                            {/* Team Name */}
                            <div>
                                <label className="block text-sm font-medium text-gray-900 mb-2">
                                    {t("signupTeamNameLabel")}
                                </label>
                                <input
                                    type="text"
                                    name="teamName"
                                    value={formData.teamName}
                                    onChange={handleInputChange}
                                    placeholder={t("signupTeamNamePlaceholder")}
                                    className={`w-full px-4 py-3 bg-gray-50 border rounded-lg text-gray-900 placeholder-gray-400 focus:outline-hidden focus:ring-2 focus:ring-gray-900 focus:bg-white transition-all ${
                                        errors.teamName
                                            ? "border-red-500"
                                            : "border-gray-200"
                                    }`}
                                    disabled={loading}
                                />
                                {errors.teamName && (
                                    <p className="text-red-500 text-sm mt-1.5">
                                        {errors.teamName}
                                    </p>
                                )}
                            </div>

                            {/* Slug */}
                            <div>
                                <label className="block text-sm font-medium text-gray-900 mb-2">
                                    {t("signupSlugLabel")}
                                </label>
                                <div
                                    className={`flex items-center bg-gray-50 border rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-gray-900 focus-within:bg-white transition-all ${
                                        errors.slug
                                            ? "border-red-500"
                                            : "border-gray-200"
                                    }`}
                                >
                                    <span className="text-gray-400 text-sm pl-4 select-none">
                                        {t("signupSlugPreview")}
                                    </span>
                                    <input
                                        type="text"
                                        name="slug"
                                        value={formData.slug}
                                        onChange={handleInputChange}
                                        placeholder={t("signupSlugPlaceholder")}
                                        className="flex-1 px-1 py-3 bg-transparent text-gray-900 placeholder-gray-400 focus:outline-hidden"
                                        disabled={loading}
                                    />
                                </div>
                                {errors.slug && (
                                    <p className="text-red-500 text-sm mt-1.5">
                                        {errors.slug}
                                    </p>
                                )}
                            </div>

                            {/* Seats */}
                            <div>
                                <label className="block text-sm font-medium text-gray-900 mb-2">
                                    {t("signupSeatsLabel")}
                                </label>
                                <div className="flex items-center">
                                    <button
                                        type="button"
                                        onClick={() => handleSeatsChange(-1)}
                                        className="w-12 h-12 flex items-center justify-center border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-40"
                                        disabled={
                                            loading || formData.seats <= 1
                                        }
                                    >
                                        <SolarIcon name="minus-linear" className="w-4 h-4 text-gray-600" />
                                    </button>
                                    <div className="flex-1 text-center">
                                        <span className="text-2xl font-semibold text-gray-900">
                                            {formData.seats}
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => handleSeatsChange(1)}
                                        className="w-12 h-12 flex items-center justify-center border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-40"
                                        disabled={
                                            loading || formData.seats >= 1000
                                        }
                                    >
                                        <SolarIcon name="add-linear" className="w-4 h-4 text-gray-600" />
                                    </button>
                                </div>
                                <p className="text-sm text-gray-400 text-center mt-2">
                                    {t("signupSeatsHelper")}
                                </p>
                            </div>

                            {/* Déclaration d'achat professionnel */}
                            <div>
                                <label className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-gray-700 dark:text-grayDark-200">
                                    <input
                                        type="checkbox"
                                        checked={professionalChecked}
                                        onChange={(event) => {
                                            setProfessionalChecked(event.target.checked);
                                            if (event.target.checked) {
                                                setShowProfessionalError(false);
                                            }
                                        }}
                                        className="mt-1 h-4 w-4 shrink-0 cursor-pointer accent-primary"
                                    />
                                    <span>{t("signupProfessionalDeclaration")}</span>
                                </label>
                                {showProfessionalError && !professionalChecked && (
                                    <p
                                        role="alert"
                                        className="mb-0 mt-2 text-xs font-medium text-red-500 dark:text-red-400"
                                    >
                                        {t("signupProfessionalRequired")}
                                    </p>
                                )}
                            </div>

                            {/* Submit */}
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-gray-900 text-white py-3.5 rounded-lg font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {loading ? (
                                    <>
                                        <SolarIcon name="refresh-linear" className="w-4 h-4 animate-spin" />
                                        {t("signupCtaLoading")}
                                    </>
                                ) : (
                                    <>
                                        {SHOULD_OFFER_FREE_TRIAL
                                            ? t("signupCta")
                                            : t("signupCtaNoTrial")}
                                        <SolarIcon name="arrow-right-linear" className="w-4 h-4" />
                                    </>
                                )}
                            </button>

                            {/* Security note */}
                            <div className="flex items-center justify-center gap-1.5 text-xs text-gray-400">
                                <SolarIcon name="lock-keyhole-minimalistic-linear" className="w-3 h-3" />
                                {t("signupSecurePayment")}
                            </div>
                        </form>

                        {/* Existing team */}
                        <p className="text-center text-sm text-gray-400 mt-8">
                            {t("signupExistingTeam")}{" "}
                            <Link
                                href={`/${locale}/teams/dashboard`}
                                className="text-gray-900 hover:underline"
                            >
                                {t("signupExistingTeamLink")}
                            </Link>
                        </p>
                    </div>

                    {/* Right: Summary */}
                    <div>
                        {/* Pricing Card */}
                        <div className="bg-gray-50 rounded-2xl p-8">
                            <div className="flex items-baseline gap-1 mb-1">
                                <span className="text-4xl font-semibold text-gray-900">
                                    {PRICE_PER_SEAT_MONTHLY}€
                                </span>
                                <span className="text-gray-500">
                                    / {t("signupSeat")} / mois
                                </span>
                            </div>
                            <p className="text-sm text-gray-400 mb-8">
                                Facturation annuelle
                            </p>

                            {/* Price breakdown */}
                            <div className="space-y-3 pb-6 border-b border-gray-200">
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">
                                        {formData.seats}{" "}
                                        {formData.seats > 1
                                            ? t("signupSeats")
                                            : t("signupSeat")}{" "}
                                        × {PRICE_PER_SEAT_YEARLY}€
                                        {t("signupPerYear")}
                                    </span>
                                    <span className="text-gray-900 font-medium">
                                        {new Intl.NumberFormat(locale).format(
                                            totalPrice,
                                        )}
                                        €
                                    </span>
                                </div>
                            </div>

                            {/* Total */}
                            <div className="flex justify-between items-baseline pt-6">
                                <span className="text-gray-900 font-medium">
                                    {t("signupTotalAnnual")}
                                </span>
                                <div className="text-right">
                                    <span className="text-2xl font-semibold text-gray-900">
                                        {new Intl.NumberFormat(locale).format(
                                            totalPrice,
                                        )}
                                        €
                                    </span>
                                    <p className="text-sm text-gray-400">
                                        {t("signupMonthlyEquivalent", {
                                            price: monthlyTotal,
                                        })}
                                    </p>
                                </div>
                            </div>

                            {/* Trial info */}
                            {SHOULD_OFFER_FREE_TRIAL && (
                                <div className="mt-6 pt-6 border-t border-gray-200">
                                    <p className="text-sm text-gray-600">
                                        <span className="font-medium text-gray-900">
                                            {t("signupTrialInfo", {
                                                days: FREE_TRIAL_DAYS,
                                            })}
                                        </span>
                                        {" — "}
                                        {t("signupTrialDescription")}
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Features */}
                        <div className="mt-8">
                            <h3 className="text-sm font-medium text-gray-900 mb-4">
                                {t("signupFeaturesTitle")}
                            </h3>
                            <ul className="space-y-3">
                                {features.map((feature, index) => (
                                    <li
                                        key={index}
                                        className="flex items-start gap-3"
                                    >
                                        <SolarIcon name="unread-linear" className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                        <span className="text-sm text-gray-600">
                                            {feature}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
